// src/blog/SanityCustomerGalleryStrip.tsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { client } from "../sanity/client";
import { urlFor } from "../sanity/image";
import Card from "../components/Resources/CardMain";
import { formatDateTime } from "../shared/util/util";

type SanityImage = {
  alt?: string;
  asset?: {
    url?: string;
  };
};

type HeroBlock = {
  customerName?: string;
  headline?: string;
  subheadline?: string;
  segment?: string;
  publishedAt?: string;
  heroImage?: SanityImage | any;
};

type CustomerCaseStudyDoc = {
  _id: string;
  slug?: { current?: string };
  hero?: HeroBlock;
};

// 🔍 Only customerCaseStudy docs, latest first
const CUSTOMER_CASE_STUDIES_QUERY = `
*[_type == "customerCaseStudy" && defined(slug.current)]
| order(hero.publishedAt desc){
  _id,
  slug,
  hero{
    customerName,
    headline,
    subheadline,
    segment,
    publishedAt,
    heroImage{
      alt,
      asset->{url}
    }
  }
}
`;

const SanityCustomerGalleryStrip: React.FC = () => {
  const [items, setItems] = useState<CustomerCaseStudyDoc[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client
      .fetch<CustomerCaseStudyDoc[]>(CUSTOMER_CASE_STUDIES_QUERY)
      .then((res) => setItems(res || []))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="mt-8 text-sm text-gray-300">
        Loading customer stories…
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="mt-8 text-sm text-gray-400">
        No customer stories published yet.
      </div>
    );
  }

  return (
    <div className="mx-auto grid w-full max-w-max items-start justify-evenly gap-4 px-2 pb-8 sm:grid-cols-2 md:gap-y-5 md:px-0 lg:grid-cols-4">
      {items.map((item) => {
        const post = item.hero || {};
        const slug = item.slug?.current || "";
        const imgSrc =
          post?.heroImage &&
          urlFor(post?.heroImage as any) // no fixed height → preserves square
            .width(480)
            .auto("format")
            .url();

        return (
          <div
            key={item._id}
            className="h-[34rem] w-full max-w-80 rounded-md border border-gray-500" 
            // ^ was h-[24rem]; increased so title + date fit like Resources cards
          >
            <Card
              link={`/customers/${slug}`}
              title={post?.headline}
              type={"Case Study"}
              image={{
                src: imgSrc,
              }}
              datePublished={formatDateTime(post?.publishedAt)}
            />
          </div>
        );
      })}
    </div>
  );
};

export default SanityCustomerGalleryStrip;
